"use client";

import {
  type ClipboardEvent,
  type FC,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";

import { cn } from "../../utils";

/**
 * Props for the OtpInput component.
 */
export interface OtpInputProps {
  /**
   * Callback triggered whenever the OTP value changes.
   * @param value - The current OTP value as a string.
   */
  onChange?: (value: string) => void;
  /**
   * Callback triggered when the OTP input is complete (all digits are filled).
   * @param value - The complete OTP value as a string.
   */
  onDone?: (value: string) => void;
  /**
   * The number of digits in the OTP input.
   * Defaults to 6. Must be a positive integer.
   */
  digits?: number;
  /**
   * Whether to display an error state for the input fields.
   */
  hasError?: boolean;
}

export const OtpInput: FC<OtpInputProps> = ({
  onChange,
  onDone,
  digits = 6,
  hasError,
}) => {
  const [otp, setOtp] = useState<string[]>(() => Array(digits).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    const otpValue = otp.join("");

    onChange?.(otpValue);

    if (otpValue.length === digits && otp.every((digit) => digit !== "")) {
      onDone?.(otpValue);
    }
  }, [otp, onChange, onDone, digits]);

  const handleChange = (index: number, value: string) => {
    if (/^[0-9]$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (index < digits - 1 && value) {
        inputRefs.current[index + 1]?.focus();
      }
    } else if (value === "") {
      // Allow backspace to clear and move to previous input
      const newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const paste = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, digits);
    if (!paste) return;

    const newOtp = Array.from({ length: digits }, (_, i) => paste[i] ?? "");
    setOtp(newOtp);
    inputRefs.current[Math.min(paste.length, digits - 1)]?.focus();
  };

  if (digits < 1) {
    return null;
  }

  return (
    <div className="ui:flex ui:gap-[6.6px]">
      {Array.from({ length: digits }).map((_, index) => (
        <input
          key={index}
          type="text"
          maxLength={1}
          pattern="\d*"
          value={otp[index]}
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-label={`Digit ${index + 1}`}
          ref={(el) => {
            inputRefs.current[index] = el!;
          }}
          className={cn(
            "ui:size-14 ui:rounded-md ui:border ui:border-divider ui:bg-transparent ui:text-center ui:text-xl ui:text-textPrimary ui:focus:outline-none",
            {
              "ui:border-error": hasError,
            },
          )}
          onPaste={handlePaste}
          onKeyDown={(e) => {
            handleKeyDown(index, e);
          }}
          onChange={(e) => {
            handleChange(index, e.target.value);
          }}
        />
      ))}
    </div>
  );
};
